import { memo, useMemo } from 'react';
// @mui
import { Box, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// components
import { Iconify } from '../../../components';

// Detect Mac vs Windows/Linux for keyboard shortcut display
const isMac = typeof navigator !== 'undefined' && (
    navigator.userAgentData?.platform?.toLowerCase().includes('mac') ||
    navigator.platform?.toLowerCase().includes('mac')
);

// Searchbar is a simple clickable trigger that opens the CommandPalette.
// All search state, debouncing, and API calls live in CommandPalette only —
// having search logic in both components caused a race condition where
// Searchbar's clearApiData() would wipe results fetched by CommandPalette.

const Searchbar = ({ onOpenCommandPalette }) => {
    const theme = useTheme();
    const shortcutLabel = useMemo(() => (isMac ? '⌘K' : 'Ctrl K'), []);

    return (
        <Box
            onClick={() => onOpenCommandPalette?.()}
            role="button"
            tabIndex={0}
            aria-label="Search leads"
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenCommandPalette?.();
                }
            }}
            sx={{
                mx: { xs: 0.5, sm: 1 },
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                pl: 1.25,
                pr: 0.75,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.grey[500], 0.08),
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                userSelect: 'none',
                '&:hover': {
                    bgcolor: alpha(theme.palette.grey[500], 0.12),
                    transform: 'scale(1.02)',
                },
                '&:active': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`,
                },
            }}
        >
            <Iconify
                icon="eva:search-fill"
                sx={{ color: 'text.disabled', width: 16, height: 16, flexShrink: 0 }}
            />
            <Chip
                label={shortcutLabel}
                size="small"
                sx={{
                    fontSize: '11px',
                    height: 20,
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                    '& .MuiChip-label': { px: 0.75 },
                    flexShrink: 0,
                }}
            />
        </Box>
    );
};

export default memo(Searchbar);
